import { Box, styled } from '@mui/material';
import { ConstructionIcon } from 'lucide-react';
import { Fragment, type JSX } from 'react';
import { MAINTENANCE_BAR_ATTRIBUTE, MAINTENANCE_BAR_SIZE } from '@/constants/layout';
import {
  MAINTENANCE_ALERT_BAR_COPY,
  MAINTENANCE_EMPHASIS_FONT_WEIGHT,
} from '@/constants/Maintenance/maintenance';
import { corePalette } from '@/themes/colours';
import { formatMaintenanceTime } from '@/utils/functions/maintenance';
import CustomTypography from '../Common/Typography';
import { MaintenancePhase } from '@/types/Maintenance/Maintenance.types';

const ICON_SIZE = 24;

const Bar = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  minHeight: MAINTENANCE_BAR_SIZE,
  padding: '0px 32px',
  backgroundColor: corePalette.yellow50,
});

const IconWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: `${ICON_SIZE}px`,
  height: `${ICON_SIZE}px`,
  color: corePalette.offBlack200,
});

const Emphasis = styled('strong')({
  fontWeight: MAINTENANCE_EMPHASIS_FONT_WEIGHT,
});

interface IProps {
  phase: MaintenancePhase;
  startsAt: string;
  expectedEndAt: string;
  leadMinutes: number;
}

export default function MaintenanceAlertBar({
  phase,
  startsAt,
  expectedEndAt,
  leadMinutes,
}: IProps): JSX.Element {
  const segments = MAINTENANCE_ALERT_BAR_COPY[phase]({
    start: formatMaintenanceTime(startsAt),
    end: formatMaintenanceTime(expectedEndAt),
    leadMinutes,
  });

  return (
    <Bar role="status" aria-live="polite" {...{ [MAINTENANCE_BAR_ATTRIBUTE]: true }}>
      <IconWrapper>
        <ConstructionIcon size={ICON_SIZE} />
      </IconWrapper>
      <CustomTypography
        variant="bodyRegular"
        sx={{
          color: corePalette.offBlack200,
          textAlign: 'center',
          fontWeight: 400,
        }}
      >
        {segments.map(({ text, emphasised }) => (
          <Fragment key={`${phase}-${text}`}>
            {emphasised ? <Emphasis>{text}</Emphasis> : text}
          </Fragment>
        ))}
      </CustomTypography>
    </Bar>
  );
}
